import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { useJsonLd } from "@/hooks/useJsonLd";

interface BlogPostData {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  image_url: string | null;
  published_at: string;
  created_at: string;
  author_name: string | null;
  tags: string[] | null;
}

export default function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  // Per-post social meta tags (og:* and twitter:*)
  useEffect(() => {
    if (!post) return;

    const url = `https://ptahub.co.uk/blog/${post.id}`;
    const image = post.image_url || "https://ptahub.co.uk/logo-full.png";

    const setMeta = (selector: string, attr: "property" | "name", key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setCanonical = (href: string) => {
      let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const prevTitle = document.title;
    document.title = `${post.title} — PTA Hub Blog`;

    setMeta('meta[name="description"]', "name", "description", post.excerpt);
    setMeta('meta[property="og:title"]', "property", "og:title", post.title);
    setMeta('meta[property="og:description"]', "property", "og:description", post.excerpt);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[property="og:type"]', "property", "og:type", "article");
    setMeta('meta[property="og:image"]', "property", "og:image", image);
    setMeta('meta[property="article:published_time"]', "property", "article:published_time", post.published_at);
    if (post.author_name) {
      setMeta('meta[property="article:author"]', "property", "article:author", post.author_name);
    }
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", post.title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", post.excerpt);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    setCanonical(url);

    return () => {
      document.title = prevTitle;
      // Reset og:type back to website for next route
      setMeta('meta[property="og:type"]', "property", "og:type", "website");
    };
  }, [post]);


  useJsonLd(
    "ld-blog-article",
    post
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          image: post.image_url || undefined,
          author: post.author_name
            ? { "@type": "Person", name: post.author_name }
            : undefined,
          datePublished: post.published_at,
          dateModified: post.published_at,
          mainEntityOfPage: `https://ptahub.co.uk/blog/${post.id}`,
          publisher: {
            "@type": "Organization",
            name: "PTA Hub",
            logo: {
              "@type": "ImageObject",
              url: "https://ptahub.co.uk/logo-full.png",
            },
          },
        }
      : null
  );

  const fetchPost = async () => {
    if (!id) return;
    
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('id', id)
      .eq('status', 'published')
      .single();
    
    if (!error && data) {
      setPost(data);
    }
    setLoading(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Render markdown content with proper formatting
  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let listItems: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let imageCounter = 0;

    const flushList = () => {
      if (listItems.length > 0) {
        if (listType === 'ul') {
          elements.push(
            <ul key={`list-${elements.length}`} className="my-6 ml-6 list-disc space-y-2">
              {listItems.map((item, i) => (
                <li key={i} className="text-lg text-foreground/90 leading-relaxed pl-2">
                  {renderInlineFormatting(item)}
                </li>
              ))}
            </ul>
          );
        } else {
          elements.push(
            <ol key={`list-${elements.length}`} className="my-6 ml-6 list-decimal space-y-2">
              {listItems.map((item, i) => (
                <li key={i} className="text-lg text-foreground/90 leading-relaxed pl-2">
                  {renderInlineFormatting(item)}
                </li>
              ))}
            </ol>
          );
        }
        listItems = [];
        listType = null;
      }
    };

    const renderInlineFormatting = (text: string): React.ReactNode => {
      // Handle bold (**text** or __text__)
      const parts: React.ReactNode[] = [];
      let remaining = text;
      let partIndex = 0;

      while (remaining.length > 0) {
        // Match bold
        const boldMatch = remaining.match(/\*\*(.+?)\*\*|__(.+?)__/);
        // Match italic
        const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)|(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/);
        // Match links
        const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

        // Find the earliest match
        let earliestMatch: RegExpMatchArray | null = null;
        let matchType: 'bold' | 'italic' | 'link' | null = null;

        if (boldMatch && boldMatch.index !== undefined) {
          earliestMatch = boldMatch;
          matchType = 'bold';
        }
        if (italicMatch && italicMatch.index !== undefined) {
          if (!earliestMatch || italicMatch.index < earliestMatch.index!) {
            earliestMatch = italicMatch;
            matchType = 'italic';
          }
        }
        if (linkMatch && linkMatch.index !== undefined) {
          if (!earliestMatch || linkMatch.index < earliestMatch.index!) {
            earliestMatch = linkMatch;
            matchType = 'link';
          }
        }

        if (earliestMatch && earliestMatch.index !== undefined) {
          // Add text before match
          if (earliestMatch.index > 0) {
            parts.push(<span key={partIndex++}>{remaining.slice(0, earliestMatch.index)}</span>);
          }

          // Add formatted match
          if (matchType === 'bold') {
            parts.push(
              <strong key={partIndex++} className="font-semibold text-foreground">
                {earliestMatch[1] || earliestMatch[2]}
              </strong>
            );
          } else if (matchType === 'italic') {
            parts.push(
              <em key={partIndex++} className="italic">
                {earliestMatch[1] || earliestMatch[2]}
              </em>
            );
          } else if (matchType === 'link') {
            parts.push(
              <a 
                key={partIndex++} 
                href={earliestMatch[2]} 
                className="text-primary hover:underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                {earliestMatch[1]}
              </a>
            );
          }

          remaining = remaining.slice(earliestMatch.index + earliestMatch[0].length);
        } else {
          // No more matches, add remaining text
          parts.push(<span key={partIndex++}>{remaining}</span>);
          break;
        }
      }

      return parts.length > 0 ? parts : text;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Skip empty lines but flush lists
      if (!trimmedLine) {
        flushList();
        continue;
      }

      // Check for markdown images ![alt](url)
      const markdownImageMatch = trimmedLine.match(/^!\[(.+?)\]\((.+?)\)$/);
      if (markdownImageMatch) {
        flushList();
        elements.push(
          <figure key={`img-${elements.length}`} className="my-8 mx-auto max-w-2xl">
            <img 
              src={markdownImageMatch[2]} 
              alt={markdownImageMatch[1]}
              className="w-full h-auto object-cover rounded-xl shadow-lg"
            />
            <figcaption className="text-center text-sm text-muted-foreground mt-3 italic">
              {markdownImageMatch[1]}
            </figcaption>
          </figure>
        );
        continue;
      }

      // Skip [IMAGE: description] placeholders - they should have been replaced with actual images
      const imageMatch = trimmedLine.match(/\[IMAGE:\s*(.+?)\]/i);
      if (imageMatch) {
        continue;
      }

      // Check for h2 (##)
      if (trimmedLine.startsWith('## ')) {
        flushList();
        const headingText = trimmedLine.slice(3);
        elements.push(
          <h2 key={`h2-${elements.length}`} className="text-2xl md:text-3xl font-bold text-foreground mt-12 mb-4 pb-2 border-b border-border/50">
            {renderInlineFormatting(headingText)}
          </h2>
        );
        continue;
      }

      // Check for h3 (###)
      if (trimmedLine.startsWith('### ')) {
        flushList();
        const headingText = trimmedLine.slice(4);
        elements.push(
          <h3 key={`h3-${elements.length}`} className="text-xl md:text-2xl font-semibold text-foreground mt-8 mb-3">
            {renderInlineFormatting(headingText)}
          </h3>
        );
        continue;
      }

      // Check for h4 (####)
      if (trimmedLine.startsWith('#### ')) {
        flushList();
        const headingText = trimmedLine.slice(5);
        elements.push(
          <h4 key={`h4-${elements.length}`} className="text-lg md:text-xl font-semibold text-foreground mt-6 mb-2">
            {renderInlineFormatting(headingText)}
          </h4>
        );
        continue;
      }

      // Check for unordered list items
      if (trimmedLine.match(/^[-*+]\s/)) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        listItems.push(trimmedLine.slice(2));
        continue;
      }

      // Check for ordered list items
      if (trimmedLine.match(/^\d+\.\s/)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        listItems.push(trimmedLine.replace(/^\d+\.\s/, ''));
        continue;
      }

      // Check for blockquote
      if (trimmedLine.startsWith('> ')) {
        flushList();
        elements.push(
          <blockquote key={`quote-${elements.length}`} className="my-6 border-l-4 border-primary pl-6 py-2 bg-primary/5 rounded-r-lg italic text-lg text-foreground/80">
            {renderInlineFormatting(trimmedLine.slice(2))}
          </blockquote>
        );
        continue;
      }

      // Check for horizontal rule
      if (trimmedLine.match(/^(---|\*\*\*|___)$/)) {
        flushList();
        elements.push(
          <hr key={`hr-${elements.length}`} className="my-8 border-border" />
        );
        continue;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p key={`p-${elements.length}`} className="text-lg text-foreground/90 leading-relaxed mb-6">
          {renderInlineFormatting(trimmedLine)}
        </p>
      );
    }

    flushList();
    return elements;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl py-12 px-4">
          <p className="text-center text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl py-12 px-4 text-center">
          <p className="text-muted-foreground mb-4">Blog post not found</p>
          <Button onClick={() => navigate('/blog')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      {post.image_url && (
        <div className="w-full h-[40vh] md:h-[50vh] relative overflow-hidden">
          <img 
            src={post.image_url} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
        </div>
      )}

      <div className="container max-w-5xl px-4 pb-16">
        {/* Back Button */}
        <div className={post.image_url ? "-mt-24 relative z-10 mb-6" : "pt-12 mb-6"}>
          <Button 
            variant="outline" 
            onClick={() => navigate('/blog')} 
            className="bg-background/80 backdrop-blur-sm hover:bg-background"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </div>

        <article className="bg-card rounded-2xl shadow-lg p-6 md:p-10 lg:p-12 space-y-6">
          {/* Header */}
          <header className="space-y-4 pb-6 border-b border-border">
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, idx) => (
                  <Badge 
                    key={idx} 
                    className="rounded-full font-medium text-xs px-3 py-1 bg-primary/10 text-primary hover:bg-primary/20"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              {post.title}
            </h1>
            
            {/* Excerpt */}
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {post.excerpt}
            </p>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pt-2">
              {post.author_name && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="font-medium text-foreground">{post.author_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <time>{formatDate(post.published_at)}</time>
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="prose-content">
            {renderContent(post.content)}
          </div>

          {/* Footer */}
          <footer className="pt-8 mt-8 border-t border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-muted-foreground text-sm">
                Thanks for reading! Share this post with your PTA community.
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/blog')}
              >
                More Articles
              </Button>
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
}
